import { isUiRole } from '@/services/entity-resolution';

/**
 * Shared role predicates (DV-2751).
 *
 * Internal roles were previously identified by ad-hoc `=== 'INTERNAL_ADMIN'`
 * comparisons scattered across the app, which is why adding
 * INTERNAL_POST_PROCESSING meant hunting down every site. Centralised here so
 * a future internal role is one edit, not an audit.
 *
 * Note the two internal roles are NOT interchangeable:
 *  - INTERNAL_ADMIN short-circuits to allow-all in `util/permissions.ts` and
 *    routes org-switching to the Admin Console.
 *  - INTERNAL_POST_PROCESSING has no blanket access — it is checked against
 *    the permission table like a customer role, and is deliberately excluded
 *    from Admin Console / account management. Unscoped, it lands on Global
 *    Orders instead (`unscopedLandingRoute`, DV-3337).
 *
 * Use this helper only where "is this an internal user" is genuinely the
 * question (e.g. asset-team tooling). Where the answer differs between the two
 * internal roles, keep the explicit comparison.
 */
export const isInternalRole = (role: string | null | undefined): boolean =>
  role === 'INTERNAL_ADMIN' || role === 'INTERNAL_POST_PROCESSING';

/**
 * True for internal roles whose remit is assets and nothing else: full CRUD on
 * assets, read-only on every other resource.
 *
 * Sole consumer today is `permissions.canCreateOnRoute`, which withholds
 * create/import/checkout affordances on non-asset surfaces for these roles.
 * Asset tooling availability is a SEPARATE axis: `assetFeatureFlags` keys off
 * `isInternalRole`, not this predicate — a new asset-only role must be added
 * to BOTH `isInternalRole` (to light up the asset tooling) and here (to keep
 * the create/checkout restrictions).
 *
 * Deliberately NOT `isInternalRole`: INTERNAL_ADMIN is unrestricted and takes
 * the allow-all short-circuit long before this check runs.
 */
export const isAssetOnlyInternalRole = (
  role: string | null | undefined,
): boolean => role === 'INTERNAL_POST_PROCESSING';

/**
 * True only for the unrestricted internal role. Prefer this over inline
 * `role === 'INTERNAL_ADMIN'` comparisons so a future
 * role change is one edit (same rationale as isInternalRole above).
 */
export const isInternalAdmin = (
  role: string | null | undefined,
): boolean => role === 'INTERNAL_ADMIN';

/**
 * DV-2549: who may pause / resume the account's PMS integration. Internal
 * Admin and Customer Admin only, per the story. Deliberately narrower than the
 * permission table's `PUT :Integration` row, which also governs credential
 * editing (allowed for CUSTOMER_FINANCE / CUSTOMER_DEVELOPER).
 *
 * Until DV-2773 #1 lands (Keycloak scope on PUT /properties/pms/{entity}/status)
 * this frontend check is the ONLY guard — the backend accepts any authenticated
 * caller. Hiding the controls is load-bearing, not cosmetic.
 */
export const canManageIntegrationStatus = (
  role: string | null | undefined,
): boolean => role === 'INTERNAL_ADMIN' || role === 'CUSTOMER_ADMIN';

/**
 * DV-3714: who gets the photos page's Share control — the Internal Share and
 * PlanOtour tabs and the gallery's quick menu. Every role the UI
 * supports (the one role table, `ROLE_SUFFIXES`): the four customer roles and
 * both internal ones. asset-service takes the same six for the public link
 * (#271, widened from DV-3480's Customer Admin and internal roles) and for the
 * team and gallery emails.
 */
export const canSharePhotoset = (role: string | null | undefined): boolean =>
  role != null && isUiRole(role);

/**
 * DV-3519: who gets Sync on the photos page — the button, the
 * "Unsynced Changes" / "Synced" chip, and the unsynced-changes prompt on
 * leaving (the button was Sync & Share until DV-3714 split Share off).
 * Everyone except Internal Post-Processing: they edit photos but do not
 * decide when the client receives them. Their uploads reach the client's
 * PMS when the order is delivered (asset-service's delivery push, with the
 * account's Auto-Sync Assets on) and through Legacy's delivered webhook;
 * anything they change after that is sent by an admin or the customer.
 *
 * UI only: the sync endpoints still accept the role.
 */
export const canSync = (role: string | null | undefined): boolean =>
  !isAssetOnlyInternalRole(role);

/**
 * DV-3537: who gets Caption Sort as a toolbar button, in place of the service
 * pills, rather than as a kebab item. Internal Post-Processing only: sorting
 * finished photosets is their main job, and they don't order services.
 *
 * An explicit comparison, deliberately NOT `isAssetOnlyInternalRole`: that
 * predicate is about create/checkout restrictions, and a future asset-only
 * role should not inherit this toolbar layout without someone deciding it.
 */
export const hasCaptionSortToolbarButton = (
  role: string | null | undefined
): boolean => role === 'INTERNAL_POST_PROCESSING';
